/**
 * Indicator Categories Configuration
 *
 * Centralized organization of technical indicators into logical categories
 * for use in dashboard builder UI components.
 */

export const INDICATOR_CATEGORIES = [
  {
    id: 'oscillators',
    label: 'Oscillators',
    description: 'Momentum and trend-following indicators',
    indicators: [
      {
        value: 'macd',
        label: 'MACD',
        description: 'Moving Average Convergence Divergence',
        tooltip: 'Shows relationship between two moving averages'
      },
      {
        value: 'rsi',
        label: 'RSI',
        description: 'Relative Strength Index',
        tooltip: 'Measures speed and magnitude of price changes'
      },
      {
        value: 'ac',
        label: 'Accelerator',
        description: 'Acceleration/Deceleration Oscillator',
        tooltip: 'Measures acceleration and deceleration of momentum'
      }
    ]
  },
  {
    id: 'volatility',
    label: 'Volatility',
    description: 'Volatility and price range indicators',
    indicators: [
      {
        value: 'bollinger',
        label: 'Bollinger Bands',
        description: 'Volatility bands around moving average',
        tooltip: 'Shows price volatility and potential overbought/oversold levels'
      }
    ]
  },
  {
    id: 'volume',
    label: 'Volume',
    description: 'Volume-based indicators',
    indicators: [
      {
        value: 'volume',
        label: 'Volume',
        description: 'Trading volume with moving average',
        tooltip: 'Shows trading activity and volume trends'
      },
      {
        value: 'vwap',
        label: 'VWAP',
        description: 'Volume Weighted Average Price',
        tooltip: 'Average price weighted by volume'
      }
    ]
  },
  {
    id: 'moving_averages',
    label: 'Moving Averages',
    description: 'Trend-following moving average overlays',
    indicators: [
      {
        value: 'sma_50',
        label: 'SMA 50',
        description: '50-period Simple Moving Average',
        tooltip: 'Short-term trend indicator'
      },
      {
        value: 'sma_200',
        label: 'SMA 200',
        description: '200-period Simple Moving Average',
        tooltip: 'Long-term trend indicator'
      },
      {
        value: 'sma_720',
        label: 'SMA 720',
        description: '720-period Simple Moving Average',
        tooltip: 'Very long-term trend indicator (~3 years daily)'
      },
      {
        value: 'ema',
        label: 'EMA',
        description: 'Exponential Moving Average',
        tooltip: 'Responsive moving average that emphasizes recent prices'
      }
    ]
  }
];

/**
 * Get the category object for a specific indicator
 * @param {string} indicatorValue - The indicator value (e.g., 'macd', 'rsi')
 * @returns {object|null} Category object or null if not found
 */
export const getCategoryForIndicator = (indicatorValue) => {
  for (const category of INDICATOR_CATEGORIES) {
    const indicator = category.indicators.find(ind => ind.value === indicatorValue);
    if (indicator) {
      return category;
    }
  }
  return null;
};

/**
 * Get a flat list of all indicator values
 * Useful for backward compatibility and quick lookups
 * @returns {string[]} Array of indicator values
 */
export const getFlatIndicatorsList = () => {
  return INDICATOR_CATEGORIES.flatMap(category =>
    category.indicators.map(ind => ind.value)
  );
};

/**
 * Get indicator metadata by value
 * @param {string} indicatorValue - The indicator value (e.g., 'macd')
 * @returns {object|null} Indicator object with label, description, etc.
 */
export const getIndicatorMetadata = (indicatorValue) => {
  for (const category of INDICATOR_CATEGORIES) {
    const indicator = category.indicators.find(ind => ind.value === indicatorValue);
    if (indicator) {
      return indicator;
    }
  }
  return null;
};

/**
 * Build a structure showing selected count per category
 * @param {string[]} selectedIndicators - Array of selected indicator values
 * @returns {object} Map of categoryId -> selected count
 */
export const getCategorySelectionCounts = (selectedIndicators) => {
  const counts = {};

  INDICATOR_CATEGORIES.forEach(category => {
    const selectedInCategory = category.indicators.filter(
      ind => selectedIndicators.includes(ind.value)
    ).length;

    counts[category.id] = selectedInCategory;
  });

  return counts;
};

/**
 * Get all indicators in a specific category
 * @param {string} categoryId - The category ID
 * @returns {object[]} Array of indicator objects
 */
export const getIndicatorsInCategory = (categoryId) => {
  const category = INDICATOR_CATEGORIES.find(cat => cat.id === categoryId);
  return category ? category.indicators : [];
};

/**
 * Format indicator display with parameters (Robinhood style)
 * @param {string} indicatorValue - The indicator name (e.g., 'macd', 'rsi')
 * @param {object} settings - The indicator settings object
 * @returns {string} Formatted display string with parameters
 */
export const formatIndicatorDisplay = (indicatorValue, settings = {}) => {
  const metadata = getIndicatorMetadata(indicatorValue);
  if (!metadata) return indicatorValue;

  // Import defaults dynamically to avoid circular dependency
  const { INDICATOR_DEFAULTS } = require('./indicatorDefaults');
  const baseDefaults = INDICATOR_DEFAULTS[indicatorValue] || {};
  const mergedSettings = { ...baseDefaults, ...settings };

  let params = '';

  switch (indicatorValue) {
    case 'macd':
      params = ` (${mergedSettings.fast || 12}, ${mergedSettings.slow || 26}, ${mergedSettings.signal || 9})`;
      break;
    case 'rsi':
      params = ` (${mergedSettings.period || 14})`;
      break;
    case 'bollinger':
      params = ` (${mergedSettings.period || 20}, ${-Math.abs(mergedSettings.deviations_lower || 2)}, ${mergedSettings.deviations_upper || 2})`;
      break;
    case 'volume':
      params = mergedSettings.ma_period ? ` (${mergedSettings.ma_period})` : '';
      break;
    case 'sma_50':
      params = ` (${mergedSettings.period || 50})`;
      break;
    case 'sma_200':
      params = ` (${mergedSettings.period || 200})`;
      break;
    case 'sma_720':
      params = ` (${mergedSettings.period || 720})`;
      break;
    case 'ema':
      params = ` (${mergedSettings.period || 12})`;
      break;
    case 'vwap':
    case 'ac':
      params = ''; // No parameters to display
      break;
    default:
      params = '';
  }

  return `${metadata.label}${params}`;
};

/**
 * Merge custom indicators into categories array
 * @param {object[]} customIndicatorsList - Array of custom indicator objects from API
 * @returns {object[]} Categories array with custom indicators category added
 */
export const mergeCustomIndicators = (customIndicatorsList) => {
  if (!customIndicatorsList || customIndicatorsList.length === 0) {
    return INDICATOR_CATEGORIES;
  }

  const customCategory = {
    id: 'custom',
    label: 'My Custom Indicators',
    description: 'Your saved custom indicator configurations',
    indicators: customIndicatorsList.map(ci => ({
      value: `custom_${ci.id}`,
      label: ci.name,
      description: `Custom ${ci.base_indicator.toUpperCase()}`,
      baseIndicator: ci.base_indicator,
      customSettings: ci.settings,
      isCustom: true,
      customId: ci.id,
      tooltip: `Based on ${ci.base_indicator.toUpperCase()} with custom settings`
    }))
  };

  // Add custom category at the top
  return [customCategory, ...INDICATOR_CATEGORIES];
};
